import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Clock3,
  Eye,
  Gauge,
  RefreshCw,
  ScanFace,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserCheck,
} from 'lucide-react';
import { Empty, ErrorBox, SkeletonTableRows } from '../ui/kit';
import './Verifications.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit } from '../lib/zeit';

// Die Entscheidung bleibt ausschließlich auf /verifications/:id. Selfies
// werden in dieser Queue nie als direkte Datei oder Query-Token ausgeliefert.
const FILTERS = [
  { key: 'open', label: 'Offen', hint: 'Manuelle Entscheidung erforderlich', icon: ScanFace },
  { key: 'done', label: 'Erledigt', hint: 'Bestätigt, abgelehnt oder gelöscht', icon: CheckCircle2 },
  { key: 'all', label: 'Alle', hint: 'Vollständige Verifizierungshistorie', icon: Eye },
];

const STATUS = {
  submitted: { label: 'Prüfung offen', tone: 'pending' },
  auto_passed: { label: 'Liveness ok · Stichprobe', tone: 'sample' },
  approved: { label: 'Bestätigt', tone: 'approved' },
  rejected: { label: 'Abgelehnt', tone: 'rejected' },
  expired: { label: 'Gelöscht · Frist', tone: 'expired' },
};

const OPEN_STATES = ['submitted', 'auto_passed'];

// FAHRER-KI-BACKEND-001 (Teil E, Owner-Freigabe 04.09.2026): Ampel neben dem Status — grün = gleiche Person
// (automatisch bestanden), „neues Foto erbeten" = Stufe 1 nicht eindeutig, rot = 2. Foto andere Person (Fahrer
// gesperrt, Mensch entscheidet), gelb = unklar nach dem 2. Foto (Mensch), grau = nicht gelaufen.
export const AI_LABEL = {
  same: 'KI: gleiche Person', different: 'KI: andere Person', unclear: 'KI: unklar', unavailable: 'KI: nicht gelaufen',
  disabled: 'KI: abgeschaltet', unsupported: 'KI: keine Referenz', error: 'KI: Fehler',
};
export const aiLabel = (ai) => {
  if (!ai) return null;
  if (ai.acted === 'retry_requested') return 'KI: neues Foto erbeten';
  if (ai.acted === 'auto_passed') return 'KI: gleiche Person — bestanden';
  // „gesperrt" NUR, wenn der Vergleich wirklich gesperrt hat (REVIEW-FIX-005) — nie aus dem Urteil allein.
  if (ai.acted === 'locked' || ai.acted === 'already_locked') return 'KI: andere Person — gesperrt';
  if (ai.gate && ai.gate.legacy_client) return `${AI_LABEL[ai.status] || `KI: ${ai.status}`} (Hinweis, alte App — keine Sperre)`;
  if (ai.stale) return `${AI_LABEL[ai.status] || `KI: ${ai.status}`} (nach Entscheid, ohne Wirkung)`;
  return AI_LABEL[ai.status] || `KI: ${ai.status}`;
};

const methodMeta = method => {
  if (method === 'liveness') return { label: 'Liveness-Signal', detail: 'App · manuell prüfen' };
  if (method === 'human') return { label: 'Menschliche Prüfung', detail: 'Direkte Einreichung' };
  return { label: method || 'Unbekannt', detail: 'Prüfquelle' };
};

const formatDateTime = value => datumUndZeit(value, { jahr: 'voll', leer: 'Kein Zeitpunkt' });

const initials = value => String(value || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

export default function Verifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [cfg, setCfg] = useState({
    active: true,
    probability: '12',
    cooldown_days: '7',
    retention_days: '365',
  });
  const [cfgSaving, setCfgSaving] = useState(false);

  const load = useCallback(() => {
    const suffix = filter === 'all' ? '' : `?status=${filter}`;
    setLoading(true);
    api.get(`/admin/verifications${suffix}`)
      .then(response => {
        setItems(response.data.verifications || []);
        setLoadError(false);
      })
      .catch(() => {
        setItems([]);
        setLoadError(true);
      })
      .finally(() => setLoading(false));

    api.get('/admin/config')
      .then(response => {
        const config = response.data.config || {};
        setCfg({
          active: (config.verif_active ?? '1') === '1',
          probability: String(Math.round(Number(config.verif_probability || 0) * 100)),
          cooldown_days: String(Math.round(Number(config.verif_cooldown_hours || 168) / 24)),
          retention_days: String(config.verif_retention_days || 365),
        });
      })
      .catch(() => {});
  }, [filter]);

  useEffect(() => {
    setVisibleLimit(25);
    load();
  }, [load]);

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      await api.patch('/admin/config', {
        updates: {
          verif_active: cfg.active ? '1' : '0',
          verif_probability: Math.max(0, Math.min(100, Number(cfg.probability || 0))) / 100,
          verif_cooldown_hours: Math.max(0, Number(cfg.cooldown_days || 0)) * 24,
          verif_retention_days: Math.max(1, Number(cfg.retention_days || 1)),
        },
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Konnte nicht gespeichert werden');
    } finally {
      setCfgSaving(false);
    }
  };

  const normalizedSearch = search.trim().toLocaleLowerCase('de');
  const matchingItems = useMemo(() => items.filter(item => {
    if (!normalizedSearch) return true;
    const haystack = [
      item.id,
      item.driver_name,
      item.driver_email,
      item.method,
      item.status,
      item.rejection_reason,
    ].filter(Boolean).join(' ').toLocaleLowerCase('de');
    return haystack.includes(normalizedSearch);
  }), [items, normalizedSearch]);

  const visibleItems = matchingItems.slice(0, visibleLimit);
  const openCount = items.filter(item => (OPEN_STATES.includes(item.status) || item.selfie_review_required === true)).length;
  const rejectedCount = items.filter(item => item.status === 'rejected').length;
  const activeFilter = FILTERS.find(item => item.key === filter) || FILTERS[0];

  return (
    <div className="verifications-page">
      <PageHeader
        title="Fahrer-Verifizierung"
        description="Geschützte Selfie-Stichproben menschlich prüfen und nachvollziehbar entscheiden."
        actions={
        <div className="verifications-heading-actions">
          <span className="is-loaded"><ScanFace size={12} /> {items.length} geladen</span>
          <span className="is-open"><Clock3 size={12} /> {openCount} offen</span>
          <span><Gauge size={12} /> max. 25 sichtbar</span>
          <button onClick={load}><RefreshCw size={13} /> Aktualisieren</button>
        </div>
      }
      />

      <section className="verifications-principle">
        <span><ShieldCheck size={17} /></span>
        <div>
          <strong>Menschliche Entscheidung · keine automatische Gesichtserkennung</strong>
          <p>Selfie und freigegebene Referenzdokumente werden erst im geschützten Vergleichsmodus geladen.</p>
        </div>
        <i>Private Blob-Vorschau</i>
      </section>

      <section className="verifications-workspaces" aria-label="Verifizierungs-Arbeitsbereiche">
        {FILTERS.map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              className={filter === item.key ? 'is-active' : ''}
              onClick={() => setFilter(item.key)}
            >
              <Icon size={16} />
              <span><strong>{item.label}</strong><small>{item.hint}</small></span>
              {filter === item.key && <b>{items.length}</b>}
            </button>
          );
        })}
      </section>

      <section className="verifications-policy-card">
        <header>
          <div>
            <span className="verifications-policy-icon"><SlidersHorizontal size={15} /></span>
            <div>
              <small>Stichproben-Regel</small>
              <strong>Online-Gate konfigurieren</strong>
            </div>
          </div>
          <label className="verifications-switch">
            <input
              type="checkbox"
              checked={cfg.active}
              onChange={event => setCfg({ ...cfg, active: event.target.checked })}
            />
            <span />
            <b>{cfg.active ? 'Aktiv' : 'Inaktiv'}</b>
          </label>
        </header>

        <div className="verifications-policy-grid">
          <label>
            <span>Wahrscheinlichkeit</span>
            <div><input aria-label="Wahrscheinlichkeit (%)" type="number" step="1" min="0" max="100" value={cfg.probability} onChange={event => setCfg({ ...cfg, probability: event.target.value })} /><b>%</b></div>
            <small>Zufällige Stichprobe beim Online-Gehen</small>
          </label>
          <label>
            <span>Cooldown</span>
            <div><input aria-label="Cooldown (Tage)" type="number" step="1" min="0" value={cfg.cooldown_days} onChange={event => setCfg({ ...cfg, cooldown_days: event.target.value })} /><b>Tage</b></div>
            <small>Keine tägliche Wiederholung</small>
          </label>
          <label>
            <span>Aufbewahrung</span>
            <div><input aria-label="Aufbewahrung (Tage)" type="number" step="1" min="1" value={cfg.retention_days} onChange={event => setCfg({ ...cfg, retention_days: event.target.value })} /><b>Tage</b></div>
            <small>Danach automatische Löschung</small>
          </label>
          <div className="verifications-policy-save">
            <span>Änderungen gelten für neue Stichproben.</span>
            <button onClick={saveCfg} disabled={cfgSaving}>
              {cfgSaving ? 'Speichern …' : 'Regel speichern'}
            </button>
          </div>
        </div>
      </section>

      <section className="verifications-control-panel">
        <div className="verifications-control-copy">
          <span><UserCheck size={15} /></span>
          <div><small>Aktuelle Queue</small><strong>{activeFilter.label}</strong></div>
        </div>
        <label className="verifications-search-field">
          <span>Fahrer, E-Mail oder Prüf-ID</span>
          <div><Search size={14} /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Queue lokal durchsuchen …" /></div>
        </label>
        <div className="verifications-result-stat">
          <small>Treffer</small><strong>{matchingItems.length}</strong><span>in geladener Queue</span>
        </div>
        <div className="verifications-result-stat">
          <small>Sichtbar</small><strong>{Math.min(visibleLimit, matchingItems.length)}</strong><span>zunächst max. 25</span>
        </div>
        <div className="verifications-result-stat">
          <small>Abgelehnt</small><strong>{rejectedCount}</strong><span>in aktueller Queue</span>
        </div>
      </section>

      <section className="verifications-directory-card">
        <header>
          <div>
            <span className="verifications-overline">Identitätsprüfungen · {activeFilter.label}</span>
            <h2>{filter === 'open' ? 'Offene Vergleichsprüfungen' : activeFilter.label}</h2>
          </div>
          <span><ShieldCheck size={12} /> Inhalte erst im Prüfmodus</span>
        </header>

        {loadError ? (
          <div className="verifications-error-wrap">
            <ErrorBox text="Verifizierungen konnten nicht geladen werden — die Queue ist nicht leer, sondern nicht erreichbar." onRetry={load} />
          </div>
        ) : (
          <div className="verifications-table-scroll">
            <table className="verifications-directory-table">
              <thead><tr><th>Fahrer</th><th>Selfie</th><th>Methode</th><th>Status</th><th>Aktion</th></tr></thead>
              <tbody>
                {loading ? (
                  <SkeletonTableRows rows={4} cols={5} />
                ) : visibleItems.length === 0 ? (
                  <tr><td colSpan={5}><Empty icon={ScanFace} title="Keine Verifizierungen" hint={normalizedSearch ? 'Suchbegriff anpassen oder zurücksetzen.' : 'Neue Selfie-Prüfungen erscheinen hier automatisch.'} /></td></tr>
                ) : visibleItems.map(item => {
                  const status = STATUS[item.status] || { label: item.status || 'Unbekannt', tone: 'unknown' };
                  const method = methodMeta(item.method);
                  const isOpen = (OPEN_STATES.includes(item.status) || item.selfie_review_required === true);
                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="verifications-driver-cell">
                          <span>{initials(item.driver_name)}</span>
                          <div>
                            <strong>{item.driver_name || 'Unbekannter Fahrer'}</strong>
                            <small>{item.driver_email || 'Keine E-Mail'}</small>
                            <i>{formatDateTime(item.submitted_at || item.created_at)} · #{item.id}</i>
                            {item.rejection_reason && <em>{item.rejection_reason}</em>}
                          </div>
                        </div>
                      </td>
                      <td>
                        {item.has_selfie ? (
                          <button className="verifications-private-preview" onClick={() => navigate(`/verifications/${item.id}`)} title="Im geschützten Prüfmodus ansehen">
                            <ShieldCheck size={15} />
                            <span>geschützt</span>
                          </button>
                        ) : <span className="verifications-no-selfie">Kein Selfie</span>}
                      </td>
                      <td>
                        <div className="verifications-method-cell">
                          <strong>{method.label}</strong>
                          <span>{method.detail}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`verifications-status is-${status.tone}`}><i /> {status.label}</span>
                        {item.ai_match && (
                          <span className={`verifications-ai is-${item.ai_match.light}`} data-testid={`ai-${item.id}`} title={Object.values(item.ai_match.results || {}).map(r => `${r.label || ''}: ${r.verdict}${r.reason ? ` — ${r.reason}` : ''}`).join(' · ') || undefined}>
                            <i /> {aiLabel(item.ai_match)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isOpen ? (
                          <button className="verifications-review-action" onClick={() => navigate(`/verifications/${item.id}`)}>
                            <ScanFace size={13} /> Vergleich öffnen
                          </button>
                        ) : (
                          <button className="verifications-detail-action" onClick={() => navigate(`/verifications/${item.id}`)}>
                            <Eye size={13} /> Details ansehen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !loadError && matchingItems.length > visibleItems.length && (
          <footer className="verifications-load-more">
            <span>{visibleItems.length} von {matchingItems.length} sichtbar</span>
            <button onClick={() => setVisibleLimit(limit => Math.min(limit + 25, matchingItems.length))}>
              Weitere {Math.min(25, matchingItems.length - visibleItems.length)} anzeigen
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
